import { useState, useCallback } from "react";
import {
  generateSemanticDarkModeColor,
  generateSemanticLightModeColor,
} from "@/utils/darkModeColorGenerator";
import { cleanBgColorValue } from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import type { ColorToken } from "../types";

export function useColorGeneration() {
  const [generatedLightColor, setGeneratedLightColor] = useState<string | null>(
    null,
  );
  const [generatedDarkColor, setGeneratedDarkColor] = useState<string | null>(
    null,
  );
  const [hasGeneratedColor, setHasGeneratedColor] = useState(false);

  const setGeneratedColorForMode = useCallback(
    (color: string, isDarkMode: boolean) => {
      if (isDarkMode) {
        setGeneratedDarkColor(cleanBgColorValue(color));
      } else {
        setGeneratedLightColor(cleanBgColorValue(color));
      }
      setHasGeneratedColor(true);
    },
    [],
  );

  const generateDarkVariant = useCallback(
    (lightToken: ColorToken | undefined) => {
      if (!lightToken) {
        console.error("Missing light token for dark variant generation");
        return;
      }

      try {
        const darkColor = generateSemanticDarkModeColor(
          lightToken.name,
          lightToken.color,
        );
        setGeneratedDarkColor(cleanBgColorValue(darkColor));
        setHasGeneratedColor(true);
      } catch (error) {
        console.error("Failed to generate dark variant:", error);
      }
    },
    [],
  );

  const generateLightVariant = useCallback(
    (darkToken: ColorToken | undefined) => {
      if (!darkToken) {
        console.error("Missing dark token for light variant generation");
        return;
      }

      try {
        const darkHex = darkToken.hexColor || darkToken.color;
        const tokenName = darkToken.name.replace("-dark", "");
        const lightHex = generateSemanticLightModeColor(tokenName, darkHex);
        setGeneratedLightColor(cleanBgColorValue(lightHex));
        setHasGeneratedColor(true);
      } catch (error) {
        console.error("Failed to generate light variant:", error);
      }
    },
    [],
  );

  const resetGeneratedColors = useCallback(() => {
    setGeneratedLightColor(null);
    setGeneratedDarkColor(null);
    setHasGeneratedColor(false);
  }, []);

  return {
    generatedLightColor,
    generatedDarkColor,
    hasGeneratedColor,
    generateDarkVariant,
    generateLightVariant,
    resetGeneratedColors,
    setGeneratedColorForMode,
  };
}
